import { A, useNavigate, useParams } from '@solidjs/router';
import { type Component, createEffect, createResource, createSignal, For, Show } from 'solid-js';
import { type Agent, type AvailableModelsResponse, createAgent, fetchAgent, fetchAvailableModels, updateAgent } from '../services/api';

interface AgentAction {
  name: string;
  description?: string;
  trigger?: string;
  steps?: string[];
}

interface Routine {
  name: string;
  schedule?: string;
  action: string;
}

interface AdaptCondition {
  when: string;
  then: string;
}

const AgentBuilder: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isEditMode = () => !!params.id;
  // Get projectId from route params (from /projects/:projectId/agents/new)
  const getProjectId = () => params.projectId;

  // ============================================================================
  // Basic Information State
  // ============================================================================
  const [name, setName] = createSignal('');
  const [purpose, setPurpose] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [version, setVersion] = createSignal('1.0.0');
  const [visibility, setVisibility] = createSignal<Agent['visibility']>('private');
  const [status, setStatus] = createSignal<Agent['status']>('draft');

  // ============================================================================
  // Identity Section State
  // ============================================================================
  const [principles, setPrinciples] = createSignal<string[]>([]);
  const [newPrinciple, setNewPrinciple] = createSignal('');
  const [personality, setPersonality] = createSignal<string[]>([]);
  const [newPersonality, setNewPersonality] = createSignal('');
  const [showIdentity, setShowIdentity] = createSignal(false);

  // ============================================================================
  // Capabilities Section State (What You Get)
  // ============================================================================
  const [artifactTypes, setArtifactTypes] = createSignal<string[]>([]);
  const [newArtifactType, setNewArtifactType] = createSignal('');
  const [goals, setGoals] = createSignal<string[]>([]);
  const [newGoal, setNewGoal] = createSignal('');
  const [showCapabilities, setShowCapabilities] = createSignal(true);

  // ============================================================================
  // Actions Section State (How It Works)
  // ============================================================================
  const [actions, setActions] = createSignal<AgentAction[]>([]);
  const [newActionName, setNewActionName] = createSignal('');
  const [newActionDescription, setNewActionDescription] = createSignal('');
  const [routines, setRoutines] = createSignal<Routine[]>([]);
  const [newRoutineName, setNewRoutineName] = createSignal('');
  const [newRoutineSchedule, setNewRoutineSchedule] = createSignal('');
  const [newRoutineAction, setNewRoutineAction] = createSignal('');
  const [adaptConditions, setAdaptConditions] = createSignal<AdaptCondition[]>([]);
  const [newAdaptWhen, setNewAdaptWhen] = createSignal('');
  const [newAdaptThen, setNewAdaptThen] = createSignal('');
  const [showActions, setShowActions] = createSignal(false);

  // ============================================================================
  // Knowledge Section State (What It Knows)
  // ============================================================================
  const [knowledge, setKnowledge] = createSignal<string[]>([]);
  const [newKnowledge, setNewKnowledge] = createSignal('');
  const [showKnowledge, setShowKnowledge] = createSignal(false);

  // ============================================================================
  // Guidance Section State
  // ============================================================================
  const [requirements, setRequirements] = createSignal<string[]>([]);
  const [newRequirement, setNewRequirement] = createSignal('');
  const [constraints, setConstraints] = createSignal<string[]>([]);
  const [newConstraint, setNewConstraint] = createSignal('');
  const [guardrails, setGuardrails] = createSignal<string[]>([]);
  const [newGuardrail, setNewGuardrail] = createSignal('');
  const [showGuidance, setShowGuidance] = createSignal(false);

  // ============================================================================
  // Advanced Section State
  // ============================================================================
  // Fetch models available to the user (filtered by API keys in their vault)
  const [modelsData] = createResource<AvailableModelsResponse>(fetchAvailableModels);
  const [provider, setProvider] = createSignal('');
  const [model, setModel] = createSignal('');
  const [temperature, setTemperature] = createSignal(0.7);
  const [maxTokens, setMaxTokens] = createSignal(8192);

  // Get available models for selected provider
  const availableModels = () => {
    const data = modelsData();
    const selectedProvider = provider();
    if (!data?.providers || !selectedProvider) return [];
    const providerData = data.providers.find(p => p.id === selectedProvider);
    return providerData?.models || [];
  };

  // Get missing providers (no API keys configured)
  const missingProviders = () => modelsData()?.missingProviders || [];
  const [memoryType, setMemoryType] = createSignal('conversation');
  const [maxMessages, setMaxMessages] = createSignal(50);
  const [executionStrategy, setExecutionStrategy] = createSignal<'prompt-only' | 'agent-loop'>(
    'agent-loop',
  );
  const [maxAgentSteps, setMaxAgentSteps] = createSignal(10);
  const [showAdvanced, setShowAdvanced] = createSignal(false);

  // ============================================================================
  // UI State
  // ============================================================================
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = createSignal(false);

  // ============================================================================
  // Load existing agent if editing
  // ============================================================================
  const [existingAgent] = createResource(
    () => (isEditMode() ? params.id : null),
    async (id) => {
      if (!id) return null;
      return fetchAgent(id);
    },
  );

  // Populate form when agent loads
  createEffect(() => {
    const agent = existingAgent();
    if (agent) {
      // Basic info
      setName(agent.name);
      setPurpose(agent.purpose || '');
      setDescription(agent.description || '');
      setVersion(agent.version);
      setVisibility(agent.visibility);
      setStatus(agent.status);

      // Identity
      if (agent.identity?.principles) {
        setPrinciples(agent.identity.principles);
        setShowIdentity(true);
      }
      if (agent.identity?.personality) {
        setPersonality(agent.identity.personality);
        setShowIdentity(true);
      }

      // Capabilities
      if (agent.artifact_types) setArtifactTypes(agent.artifact_types);
      if (agent.goals) setGoals(agent.goals);

      // Actions
      if (agent.actions) {
        setActions(agent.actions);
        setShowActions(true);
      }
      if (agent.routines) {
        setRoutines(agent.routines);
        setShowActions(true);
      }
      if (agent.adapt_conditions) {
        setAdaptConditions(agent.adapt_conditions);
        setShowActions(true);
      }

      // Knowledge
      if (agent.knowledge) {
        setKnowledge(agent.knowledge);
        setShowKnowledge(true);
      }

      // Guidance
      if (agent.guidance?.requirements) {
        setRequirements(agent.guidance.requirements);
        setShowGuidance(true);
      }
      if (agent.guidance?.constraints) {
        setConstraints(agent.guidance.constraints as string[]);
        setShowGuidance(true);
      }
      if (agent.guidance?.guardrails) {
        setGuardrails(agent.guidance.guardrails);
        setShowGuidance(true);
      }

      // Advanced
      // Load preferred provider and model (check both camelCase and snake_case)
      const agentProvider = agent.preferredProvider || agent.preferred_provider;
      const agentModel = agent.preferredModel || agent.preferred_model || agent.properties?.model;
      if (agentProvider) {
        setProvider(agentProvider);
        setShowAdvanced(true);
      }
      if (agentModel) {
        setModel(agentModel);
        setShowAdvanced(true);
      }
      if (agent.properties?.temperature !== undefined) {
        setTemperature(agent.properties.temperature);
        setShowAdvanced(true);
      }
      if (agent.properties?.maxTokens !== undefined) {
        setMaxTokens(agent.properties.maxTokens);
        setShowAdvanced(true);
      }
      if (agent.memory_config?.type) {
        setMemoryType(agent.memory_config.type);
        setShowAdvanced(true);
      }
      if (agent.memory_config?.maxMessages !== undefined) {
        setMaxMessages(agent.memory_config.maxMessages);
        setShowAdvanced(true);
      }

      // Execution Strategy (note: backend uses snake_case)
      if (agent.execution_strategy) {
        setExecutionStrategy(agent.execution_strategy as 'prompt-only' | 'agent-loop');
        setShowAdvanced(true);
      }
      if (agent.max_agent_steps !== undefined) {
        setMaxAgentSteps(agent.max_agent_steps);
        setShowAdvanced(true);
      }

      setHasUnsavedChanges(false);
    }
  });

  // Track changes
  const markChanged = () => setHasUnsavedChanges(true);

  // ============================================================================
  // List Management Helpers
  // ============================================================================
  const addToList = (
    getter: () => string[],
    setter: (items: string[]) => void,
    newValue: () => string,
    clearNew: () => void,
  ) => {
    const item = newValue().trim();
    if (item && !getter().includes(item)) {
      setter([...getter(), item]);
      clearNew();
      markChanged();
    }
  };

  const removeFromList = (
    getter: () => string[],
    setter: (items: string[]) => void,
    index: number,
  ) => {
    setter(getter().filter((_, i) => i !== index));
    markChanged();
  };

  // ============================================================================
  // Action Management
  // ============================================================================
  const addAction = () => {
    const actionName = newActionName().trim();
    if (actionName) {
      setActions([
        ...actions(),
        {
          name: actionName,
          description: newActionDescription().trim() || undefined,
        },
      ]);
      setNewActionName('');
      setNewActionDescription('');
      markChanged();
    }
  };

  const removeAction = (index: number) => {
    setActions(actions().filter((_, i) => i !== index));
    markChanged();
  };

  // ============================================================================
  // Routine Management
  // ============================================================================
  const addRoutine = () => {
    const routineName = newRoutineName().trim();
    const routineAction = newRoutineAction().trim();
    if (routineName && routineAction) {
      setRoutines([
        ...routines(),
        {
          name: routineName,
          schedule: newRoutineSchedule().trim() || undefined,
          action: routineAction,
        },
      ]);
      setNewRoutineName('');
      setNewRoutineSchedule('');
      setNewRoutineAction('');
      markChanged();
    }
  };

  const removeRoutine = (index: number) => {
    setRoutines(routines().filter((_, i) => i !== index));
    markChanged();
  };

  // ============================================================================
  // Adapt Condition Management
  // ============================================================================
  const addAdaptCondition = () => {
    const when = newAdaptWhen().trim();
    const then = newAdaptThen().trim();
    if (when && then) {
      setAdaptConditions([...adaptConditions(), { when, then }]);
      setNewAdaptWhen('');
      setNewAdaptThen('');
      markChanged();
    }
  };

  const removeAdaptCondition = (index: number) => {
    setAdaptConditions(adaptConditions().filter((_, i) => i !== index));
    markChanged();
  };

  // ============================================================================
  // Save Handler
  // ============================================================================
  const handleSave = async () => {
    if (!name().trim()) {
      setError('Agent name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const agentData: Partial<Agent> = {
        name: name().trim(),
        purpose: purpose().trim() || undefined,
        description: description().trim() || undefined,
        version: version(),
        visibility: visibility(),
        status: status(),

        // Identity
        identity:
          principles().length > 0 || personality().length > 0
            ? {
                principles: principles().length > 0 ? principles() : undefined,
                personality: personality().length > 0 ? personality() : undefined,
              }
            : undefined,

        // Capabilities
        artifact_types: artifactTypes().length > 0 ? artifactTypes() : undefined,
        goals: goals().length > 0 ? goals() : undefined,

        // Actions
        actions: actions().length > 0 ? actions() : undefined,
        routines: routines().length > 0 ? routines() : undefined,
        adapt_conditions: adaptConditions().length > 0 ? adaptConditions() : undefined,

        // Knowledge
        knowledge: knowledge().length > 0 ? knowledge() : undefined,

        // Guidance
        guidance:
          requirements().length > 0 || constraints().length > 0 || guardrails().length > 0
            ? {
                requirements: requirements().length > 0 ? requirements() : undefined,
                constraints: constraints().length > 0 ? constraints() : undefined,
                guardrails: guardrails().length > 0 ? guardrails() : undefined,
              }
            : undefined,

        // Preferred LLM provider and model
        preferredProvider: provider() || undefined,
        preferredModel: model() || undefined,

        // Properties (temperature, maxTokens)
        properties:
          temperature() !== 0.7 || maxTokens() !== 4096
            ? {
                temperature: temperature(),
                maxTokens: maxTokens(),
              }
            : undefined,

        // Memory
        memory_config:
          memoryType() !== 'conversation' || maxMessages() !== 50
            ? {
                type: memoryType(),
                maxMessages: maxMessages(),
              }
            : undefined,

        // Execution Strategy
        executionStrategy: executionStrategy(),
        maxAgentSteps: executionStrategy() === 'agent-loop' ? maxAgentSteps() : undefined,

        // Project association
        projectId: getProjectId() || undefined,
      };

      if (isEditMode()) {
        await updateAgent(params.id, agentData);
      } else {
        await createAgent(agentData);
      }

      setHasUnsavedChanges(false);
      // Navigate back to project if we came from one, otherwise to agents list
      if (getProjectId()) {
        navigate(`/projects/${getProjectId()}`);
      } else {
        navigate('/agents');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save agent');
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================================================
  // Completion Score
  // ============================================================================
  const getCompletionScore = () => {
    let score = 0;
    if (name().trim()) score += 25;
    if (purpose().trim()) score += 25;
    if (artifactTypes().length > 0 || goals().length > 0) score += 25;
    if (actions().length > 0 || knowledge().length > 0) score += 25;
    return score;
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div class="max-w-4xl mx-auto">
      {/* Header */}
      <div class="mb-6">
        <div class="flex items-center gap-4 mb-4">
          <A
            href="/agents"
            class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </A>
          <div class="flex-1">
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {isEditMode() ? 'Edit Agent' : 'Create Agent'}
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              {isEditMode()
                ? 'Update your agent definition'
                : 'Define an AI agent with specific capabilities and behavior'}
            </p>
          </div>
          <Show when={hasUnsavedChanges()}>
            <span class="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs">
              Unsaved changes
            </span>
          </Show>
        </div>

        {/* Progress indicator */}
        <div class="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-400">Readiness</span>
            <span class="text-sm font-medium text-gray-300">{getCompletionScore()}%</span>
          </div>
          <div class="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
              style={{ width: `${getCompletionScore()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Error message */}
      <Show when={error()}>
        <div class="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
          {error()}
        </div>
      </Show>

      {/* Loading state for edit mode */}
      <Show when={isEditMode() && existingAgent.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      </Show>

      {/* Form */}
      <Show when={!isEditMode() || !existingAgent.loading}>
        <div class="space-y-6">
          {/* ================================================================== */}
          {/* Basic Information Section */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
            <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-purple-400"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Basic Information
            </h2>

            <div class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Agent Name <span class="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={name()}
                    onInput={(e) => {
                      setName(e.currentTarget.value);
                      markChanged();
                    }}
                    placeholder="e.g., Customer Support Agent"
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Version</label>
                  <input
                    type="text"
                    value={version()}
                    onInput={(e) => {
                      setVersion(e.currentTarget.value);
                      markChanged();
                    }}
                    placeholder="1.0.0"
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">
                  Purpose <span class="text-red-400">*</span>
                </label>
                <textarea
                  value={purpose()}
                  onInput={(e) => {
                    setPurpose(e.currentTarget.value);
                    markChanged();
                  }}
                  placeholder="What is this agent's primary purpose? What problem does it solve?"
                  rows={2}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={description()}
                  onInput={(e) => {
                    setDescription(e.currentTarget.value);
                    markChanged();
                  }}
                  placeholder="Detailed description of the agent's capabilities and use cases"
                  rows={3}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={status()}
                    onChange={(e) => {
                      setStatus(e.currentTarget.value as Agent['status']);
                      markChanged();
                    }}
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              <p class="text-xs text-gray-500 mt-3">
                Note: Agent visibility is inherited from the project it belongs to.
              </p>
            </div>
          </section>

          {/* ================================================================== */}
          {/* Capabilities Section (What You Get) */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowCapabilities(!showCapabilities())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-pink-400"
                >
                  <path d="M9 12h.01" />
                  <path d="M15 12h.01" />
                  <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
                  <path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1" />
                </svg>
                <span class="text-lg font-medium text-gray-200">What You Get</span>
                <Show when={artifactTypes().length > 0 || goals().length > 0}>
                  <span class="ml-2 px-2 py-0.5 bg-pink-500/20 text-pink-400 text-xs rounded-full">
                    {artifactTypes().length + goals().length} items
                  </span>
                </Show>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${
                  showCapabilities() ? 'rotate-180' : ''
                }`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showCapabilities()}>
              <div class="px-6 pb-6 space-y-6 border-t border-gray-700 pt-4">
                {/* Artifact Types */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Output Types</label>
                  <p class="text-xs text-gray-500 mb-3">
                    What types of artifacts does this agent produce?
                  </p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newArtifactType()}
                      onInput={(e) => setNewArtifactType(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(artifactTypes, setArtifactTypes, newArtifactType, () =>
                          setNewArtifactType(''),
                        )
                      }
                      placeholder="e.g., report, email, code, expression"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(artifactTypes, setArtifactTypes, newArtifactType, () =>
                          setNewArtifactType(''),
                        )
                      }
                      disabled={!newArtifactType().trim()}
                      class="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <For each={artifactTypes()}>
                      {(item, index) => (
                        <span class="inline-flex items-center gap-1 px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-sm">
                          {item}
                          <button
                            type="button"
                            onClick={() => removeFromList(artifactTypes, setArtifactTypes, index())}
                            class="p-0.5 hover:bg-pink-500/30 rounded-full"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </span>
                      )}
                    </For>
                  </div>
                </div>

                {/* Goals */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Goals</label>
                  <p class="text-xs text-gray-500 mb-3">What does this agent aim to achieve?</p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newGoal()}
                      onInput={(e) => setNewGoal(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(goals, setGoals, newGoal, () => setNewGoal(''))
                      }
                      placeholder="e.g., Resolve customer issues quickly"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => addToList(goals, setGoals, newGoal, () => setNewGoal(''))}
                      disabled={!newGoal().trim()}
                      class="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="space-y-2">
                    <For each={goals()}>
                      {(item, index) => {
                        const [isEditing, setIsEditing] = createSignal(false);
                        const [editValue, setEditValue] = createSignal(item);

                        const saveEdit = () => {
                          const trimmed = editValue().trim();
                          if (trimmed && trimmed !== item) {
                            const newGoals = [...goals()];
                            newGoals[index()] = trimmed;
                            setGoals(newGoals);
                            markChanged();
                          }
                          setIsEditing(false);
                        };

                        return (
                          <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="text-purple-400 flex-shrink-0"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <circle cx="12" cy="12" r="6" />
                              <circle cx="12" cy="12" r="2" />
                            </svg>
                            <Show when={isEditing()} fallback={
                              <span
                                class="flex-1 text-sm text-gray-300 cursor-pointer hover:text-purple-300 transition-colors"
                                onClick={() => {
                                  setEditValue(item);
                                  setIsEditing(true);
                                }}
                                title="Click to edit"
                              >
                                {item}
                              </span>
                            }>
                              <input
                                type="text"
                                value={editValue()}
                                onInput={(e) => setEditValue(e.currentTarget.value)}
                                onBlur={saveEdit}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit();
                                  if (e.key === 'Escape') setIsEditing(false);
                                }}
                                class="flex-1 px-2 py-1 rounded bg-gray-800 border border-purple-500 text-gray-200 text-sm focus:outline-none"
                                autofocus
                              />
                            </Show>
                            <button
                              type="button"
                              onClick={() => removeFromList(goals, setGoals, index())}
                              class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                              </svg>
                            </button>
                          </div>
                        );
                      }}
                    </For>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* ================================================================== */}
          {/* Identity Section */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowIdentity(!showIdentity())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-indigo-400"
                >
                  <circle cx="12" cy="8" r="5" />
                  <path d="M20 21a8 8 0 1 0-16 0" />
                </svg>
                <span class="text-lg font-medium text-gray-200">Identity & Personality</span>
                <span class="text-xs text-gray-500">(Optional)</span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showIdentity() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showIdentity()}>
              <div class="px-6 pb-6 space-y-6 border-t border-gray-700 pt-4">
                {/* Principles */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Principles</label>
                  <p class="text-xs text-gray-500 mb-3">
                    Core values and beliefs that guide this agent's behavior
                  </p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newPrinciple()}
                      onInput={(e) => setNewPrinciple(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(principles, setPrinciples, newPrinciple, () =>
                          setNewPrinciple(''),
                        )
                      }
                      placeholder="e.g., Always be helpful and honest"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(principles, setPrinciples, newPrinciple, () =>
                          setNewPrinciple(''),
                        )
                      }
                      disabled={!newPrinciple().trim()}
                      class="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="space-y-2">
                    <For each={principles()}>
                      {(item, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <span class="flex-1 text-sm text-gray-300">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeFromList(principles, setPrinciples, index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>

                {/* Personality */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Personality Traits
                  </label>
                  <p class="text-xs text-gray-500 mb-3">
                    How should this agent communicate and behave?
                  </p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newPersonality()}
                      onInput={(e) => setNewPersonality(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(personality, setPersonality, newPersonality, () =>
                          setNewPersonality(''),
                        )
                      }
                      placeholder="e.g., Friendly, Professional, Concise"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(personality, setPersonality, newPersonality, () =>
                          setNewPersonality(''),
                        )
                      }
                      disabled={!newPersonality().trim()}
                      class="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <For each={personality()}>
                      {(item, index) => (
                        <span class="inline-flex items-center gap-1 px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-full text-sm">
                          {item}
                          <button
                            type="button"
                            onClick={() => removeFromList(personality, setPersonality, index())}
                            class="p-0.5 hover:bg-indigo-500/30 rounded-full"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </span>
                      )}
                    </For>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* ================================================================== */}
          {/* Actions Section (How It Works) */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowActions(!showActions())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-amber-400"
                >
                  <path d="m12 3-1.9 5.8a2 2 0 0 1-1.287 1.288L3 12l5.8 1.9a2 2 0 0 1 1.288 1.287L12 21l1.9-5.8a2 2 0 0 1 1.287-1.288L21 12l-5.8-1.9a2 2 0 0 1-1.288-1.287Z" />
                </svg>
                <span class="text-lg font-medium text-gray-200">How It Works</span>
                <span class="text-xs text-gray-500">(Optional)</span>
                <Show when={actions().length > 0 || routines().length > 0}>
                  <span class="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                    {actions().length + routines().length} defined
                  </span>
                </Show>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showActions() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showActions()}>
              <div class="px-6 pb-6 space-y-6 border-t border-gray-700 pt-4">
                {/* Actions */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Actions</label>
                  <p class="text-xs text-gray-500 mb-3">Specific actions this agent can perform</p>

                  <div class="space-y-2 mb-3">
                    <input
                      type="text"
                      value={newActionName()}
                      onInput={(e) => setNewActionName(e.currentTarget.value)}
                      placeholder="Action name (e.g., Send Email)"
                      class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <div class="flex gap-2">
                      <input
                        type="text"
                        value={newActionDescription()}
                        onInput={(e) => setNewActionDescription(e.currentTarget.value)}
                        placeholder="Description (optional)"
                        class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      />
                      <button
                        type="button"
                        onClick={addAction}
                        disabled={!newActionName().trim()}
                        class="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div class="space-y-2">
                    <For each={actions()}>
                      {(action, index) => (
                        <div class="flex items-start gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-amber-400 flex-shrink-0 mt-1"
                          >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                          </svg>
                          <div class="flex-1">
                            <span class="text-sm font-medium text-gray-200">{action.name}</span>
                            <Show when={action.description}>
                              <p class="text-xs text-gray-500 mt-0.5">{action.description}</p>
                            </Show>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAction(index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>

                {/* Routines */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Routines</label>
                  <p class="text-xs text-gray-500 mb-3">
                    Scheduled or recurring tasks the agent performs
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                    <input
                      type="text"
                      value={newRoutineName()}
                      onInput={(e) => setNewRoutineName(e.currentTarget.value)}
                      placeholder="Routine name"
                      class="px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <input
                      type="text"
                      value={newRoutineSchedule()}
                      onInput={(e) => setNewRoutineSchedule(e.currentTarget.value)}
                      placeholder="Schedule (e.g., daily, weekly)"
                      class="px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <div class="flex gap-2">
                      <input
                        type="text"
                        value={newRoutineAction()}
                        onInput={(e) => setNewRoutineAction(e.currentTarget.value)}
                        placeholder="Action to perform"
                        class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      />
                      <button
                        type="button"
                        onClick={addRoutine}
                        disabled={!newRoutineName().trim() || !newRoutineAction().trim()}
                        class="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div class="space-y-2">
                    <For each={routines()}>
                      {(routine, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-amber-400 flex-shrink-0"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          <span class="text-sm font-medium text-gray-200">{routine.name}</span>
                          <Show when={routine.schedule}>
                            <span class="text-xs px-2 py-0.5 bg-gray-700 rounded text-gray-400">
                              {routine.schedule}
                            </span>
                          </Show>
                          <span class="text-xs text-gray-500">→ {routine.action}</span>
                          <div class="flex-1" />
                          <button
                            type="button"
                            onClick={() => removeRoutine(index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>

                {/* Adapt Conditions */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Adaptive Behavior
                  </label>
                  <p class="text-xs text-gray-500 mb-3">Conditions that trigger special behavior</p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                    <input
                      type="text"
                      value={newAdaptWhen()}
                      onInput={(e) => setNewAdaptWhen(e.currentTarget.value)}
                      placeholder="When... (condition)"
                      class="px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <div class="flex gap-2">
                      <input
                        type="text"
                        value={newAdaptThen()}
                        onInput={(e) => setNewAdaptThen(e.currentTarget.value)}
                        placeholder="Then... (action)"
                        class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      />
                      <button
                        type="button"
                        onClick={addAdaptCondition}
                        disabled={!newAdaptWhen().trim() || !newAdaptThen().trim()}
                        class="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div class="space-y-2">
                    <For each={adaptConditions()}>
                      {(condition, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <span class="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                            When
                          </span>
                          <span class="text-sm text-gray-300">{condition.when}</span>
                          <span class="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                            Then
                          </span>
                          <span class="text-sm text-gray-300">{condition.then}</span>
                          <div class="flex-1" />
                          <button
                            type="button"
                            onClick={() => removeAdaptCondition(index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* ================================================================== */}
          {/* Knowledge Section */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowKnowledge(!showKnowledge())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-cyan-400"
                >
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
                </svg>
                <span class="text-lg font-medium text-gray-200">What It Knows</span>
                <span class="text-xs text-gray-500">(Optional)</span>
                <Show when={knowledge().length > 0}>
                  <span class="ml-2 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                    {knowledge().length} items
                  </span>
                </Show>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showKnowledge() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showKnowledge()}>
              <div class="px-6 pb-6 space-y-4 border-t border-gray-700 pt-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Domain Knowledge
                  </label>
                  <p class="text-xs text-gray-500 mb-3">
                    What domain knowledge should this agent have?
                  </p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newKnowledge()}
                      onInput={(e) => setNewKnowledge(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(knowledge, setKnowledge, newKnowledge, () => setNewKnowledge(''))
                      }
                      placeholder="e.g., Company policies, Product documentation"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(knowledge, setKnowledge, newKnowledge, () => setNewKnowledge(''))
                      }
                      disabled={!newKnowledge().trim()}
                      class="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="space-y-2">
                    <For each={knowledge()}>
                      {(item, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-cyan-400 flex-shrink-0"
                          >
                            <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
                          </svg>
                          <span class="flex-1 text-sm text-gray-300">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeFromList(knowledge, setKnowledge, index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                    <Show when={knowledge().length === 0}>
                      <p class="text-sm text-gray-500 italic py-4 text-center">
                        No knowledge items added yet. Add domain knowledge for the agent.
                      </p>
                    </Show>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* ================================================================== */}
          {/* Guidance Section */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowGuidance(!showGuidance())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-rose-400"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                </svg>
                <span class="text-lg font-medium text-gray-200">Guidance & Guardrails</span>
                <span class="text-xs text-gray-500">(Optional)</span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showGuidance() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showGuidance()}>
              <div class="px-6 pb-6 space-y-6 border-t border-gray-700 pt-4">
                {/* Requirements */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Requirements</label>
                  <p class="text-xs text-gray-500 mb-3">What must this agent always do?</p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newRequirement()}
                      onInput={(e) => setNewRequirement(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(requirements, setRequirements, newRequirement, () =>
                          setNewRequirement(''),
                        )
                      }
                      placeholder="e.g., Always verify user identity"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(requirements, setRequirements, newRequirement, () =>
                          setNewRequirement(''),
                        )
                      }
                      disabled={!newRequirement().trim()}
                      class="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="space-y-2">
                    <For each={requirements()}>
                      {(item, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-green-400 flex-shrink-0"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span class="flex-1 text-sm text-gray-300">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeFromList(requirements, setRequirements, index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>

                {/* Constraints */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Constraints</label>
                  <p class="text-xs text-gray-500 mb-3">Limitations on what the agent can do</p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newConstraint()}
                      onInput={(e) => setNewConstraint(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(constraints, setConstraints, newConstraint, () =>
                          setNewConstraint(''),
                        )
                      }
                      placeholder="e.g., Cannot access financial data"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(constraints, setConstraints, newConstraint, () =>
                          setNewConstraint(''),
                        )
                      }
                      disabled={!newConstraint().trim()}
                      class="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <For each={constraints()}>
                      {(item, index) => (
                        <span class="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm">
                          {item}
                          <button
                            type="button"
                            onClick={() => removeFromList(constraints, setConstraints, index())}
                            class="p-0.5 hover:bg-orange-500/30 rounded-full"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </span>
                      )}
                    </For>
                  </div>
                </div>

                {/* Guardrails */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Guardrails</label>
                  <p class="text-xs text-gray-500 mb-3">Safety measures and behavior boundaries</p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newGuardrail()}
                      onInput={(e) => setNewGuardrail(e.currentTarget.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        addToList(guardrails, setGuardrails, newGuardrail, () =>
                          setNewGuardrail(''),
                        )
                      }
                      placeholder="e.g., Escalate to human if confidence < 80%"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addToList(guardrails, setGuardrails, newGuardrail, () =>
                          setNewGuardrail(''),
                        )
                      }
                      disabled={!newGuardrail().trim()}
                      class="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="space-y-2">
                    <For each={guardrails()}>
                      {(item, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-rose-400 flex-shrink-0"
                          >
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                          </svg>
                          <span class="flex-1 text-sm text-gray-300">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeFromList(guardrails, setGuardrails, index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* ================================================================== */}
          {/* Advanced Section */}
          {/* ================================================================== */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-gray-400"
                >
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span class="text-lg font-medium text-gray-200">Advanced Settings</span>
                <span class="text-xs text-gray-500">(Optional)</span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showAdvanced() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showAdvanced()}>
              <div class="px-6 pb-6 space-y-6 border-t border-gray-700 pt-4">
                {/* Model Settings */}
                <div>
                  <h3 class="text-sm font-medium text-gray-300 mb-4">Model Configuration</h3>
                  <Show when={modelsData.loading}>
                    <div class="flex items-center gap-2 text-sm text-gray-400 mb-4">
                      <div class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                      Loading available models...
                    </div>
                  </Show>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Provider</label>
                      <select
                        value={provider()}
                        onChange={(e) => {
                          setProvider(e.currentTarget.value);
                          setModel(''); // Reset model when provider changes
                          markChanged();
                        }}
                        class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      >
                        <option value="">Default (Google)</option>
                        <For each={modelsData()?.providers || []}>
                          {(p) => <option value={p.id}>{p.name}</option>}
                        </For>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Model</label>
                      <select
                        value={model()}
                        onChange={(e) => {
                          setModel(e.currentTarget.value);
                          markChanged();
                        }}
                        disabled={!provider()}
                        class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">{provider() ? 'Select model' : 'Select provider first'}</option>
                        <For each={availableModels()}>
                          {(m) => (
                            <option value={m.id}>
                              {m.displayName}
                              {m.status === 'preview' ? ' (Preview)' : ''}
                            </option>
                          )}
                        </For>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs text-gray-400 mb-2">
                        Temperature: {temperature().toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={temperature()}
                        onInput={(e) => {
                          setTemperature(Number.parseFloat(e.currentTarget.value));
                          markChanged();
                        }}
                        class="w-full"
                      />
                    </div>

                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Max Tokens</label>
                      <input
                        type="number"
                        min="100"
                        max="128000"
                        step="100"
                        value={maxTokens()}
                        onInput={(e) => {
                          setMaxTokens(Number.parseInt(e.currentTarget.value, 10) || 4096);
                          markChanged();
                        }}
                        class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      />
                    </div>
                  </div>

                  {/* Missing providers hint */}
                  <Show when={missingProviders().length > 0}>
                    <div class="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p class="text-xs text-yellow-400">
                        More providers available:
                        <For each={missingProviders()}>
                          {(p, i) => (
                            <>
                              {i() > 0 ? ', ' : ' '}
                              <a href={p.getKeyUrl} target="_blank" rel="noopener noreferrer" class="underline hover:text-yellow-300">
                                {p.name}
                              </a>
                            </>
                          )}
                        </For>
                        . Add API keys in{' '}
                        <A href="/vault" class="underline hover:text-yellow-300">Vault</A> to enable them.
                      </p>
                    </div>
                  </Show>
                </div>

                {/* Execution Strategy */}
                <div>
                  <h3 class="text-sm font-medium text-gray-300 mb-2">Execution Strategy</h3>
                  <p class="text-xs text-gray-500 mb-4">How the agent processes requests and makes decisions</p>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Strategy</label>
                      <select
                        value={executionStrategy()}
                        onChange={(e) => {
                          setExecutionStrategy(
                            e.currentTarget.value as 'prompt-only' | 'agent-loop',
                          );
                          markChanged();
                        }}
                        class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      >
                        <option value="agent-loop">Agent Loop (Think → Plan → Execute)</option>
                        <option value="prompt-only">Prompt Only (Direct LLM)</option>
                      </select>
                      <div class="mt-2 p-2 bg-gray-900/30 rounded text-xs text-gray-400">
                        {executionStrategy() === 'agent-loop' ? (
                          <>
                            <strong class="text-gray-300">Agent Loop:</strong> Enables multi-step reasoning. The agent can think, plan, use tools, and iterate until the task is complete. Best for complex tasks requiring multiple actions.
                          </>
                        ) : (
                          <>
                            <strong class="text-gray-300">Prompt Only:</strong> Single LLM call with no tool usage. The agent generates a direct response. Best for simple Q&A or text generation tasks.
                          </>
                        )}
                      </div>
                    </div>
                    <Show when={executionStrategy() === 'agent-loop'}>
                      <div>
                        <label class="block text-xs text-gray-400 mb-2">Max Steps</label>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={maxAgentSteps()}
                          onInput={(e) => {
                            setMaxAgentSteps(Number.parseInt(e.currentTarget.value, 10) || 10);
                            markChanged();
                          }}
                          class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                        />
                        <p class="text-xs text-gray-500 mt-1">
                          Maximum iterations before stopping
                        </p>
                      </div>
                    </Show>
                  </div>
                </div>

                {/* Memory Settings */}
                <div>
                  <h3 class="text-sm font-medium text-gray-300 mb-2">Memory Configuration</h3>
                  <p class="text-xs text-gray-500 mb-4">How the agent remembers previous interactions during execution</p>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Memory Type</label>
                      <select
                        value={memoryType()}
                        onChange={(e) => {
                          setMemoryType(e.currentTarget.value);
                          markChanged();
                        }}
                        class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      >
                        <option value="conversation">Conversation</option>
                        <option value="summary">Summary</option>
                        <option value="buffer">Buffer</option>
                        <option value="none">None</option>
                      </select>
                      <div class="mt-2 p-2 bg-gray-900/30 rounded text-xs text-gray-400">
                        {memoryType() === 'conversation' && (
                          <>
                            <strong class="text-gray-300">Conversation:</strong> Keeps recent messages as-is. Good for maintaining conversational context and multi-turn interactions.
                          </>
                        )}
                        {memoryType() === 'summary' && (
                          <>
                            <strong class="text-gray-300">Summary:</strong> Periodically summarizes older messages to save tokens while retaining key information. Good for long-running sessions.
                          </>
                        )}
                        {memoryType() === 'buffer' && (
                          <>
                            <strong class="text-gray-300">Buffer:</strong> Fixed-size sliding window of messages. Older messages are discarded. Good for tasks where recent context matters most.
                          </>
                        )}
                        {memoryType() === 'none' && (
                          <>
                            <strong class="text-gray-300">None:</strong> No memory between interactions. Each request is independent. Good for stateless, single-turn tasks.
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <label class="block text-xs text-gray-400 mb-2">
                        Max Messages
                        <span class="ml-2 text-purple-400">({maxMessages()} / 1000)</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={maxMessages()}
                        onInput={(e) => {
                          setMaxMessages(Number.parseInt(e.currentTarget.value, 10) || 50);
                          markChanged();
                        }}
                        class="w-full px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      />
                      <div class="mt-2 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          class="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all"
                          style={{ width: `${Math.min((maxMessages() / 1000) * 100, 100)}%` }}
                        />
                      </div>
                      <p class="text-xs text-gray-500 mt-1">
                        Maximum messages to retain in memory
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* ================================================================== */}
          {/* Action Buttons */}
          {/* ================================================================== */}
          <div class="flex items-center justify-between pt-4">
            <A
              href="/agents"
              class="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium transition-colors"
            >
              Cancel
            </A>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving() || !name().trim()}
              class="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving() ? (
                <span class="flex items-center gap-2">
                  <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </span>
              ) : (
                <span>{isEditMode() ? 'Save Changes' : 'Create Agent'}</span>
              )}
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default AgentBuilder;
